import { memo, useEffect, useState } from 'react';
import type { CompileResult, Proposal } from '../engine/types';
import { useI18n, type MessageKey, type Translate } from '../i18n';
import { canonicalParts, oracleSlots, reviewReason, type ReviewEntry } from '../lib/review';
import { rowTone } from '../lib/rowStatus';
import { Icon } from '../ui/Icon';
import { SizeBadges } from './Findings';
import { RowLabel } from './RowLabel';
import { ToneGlyph } from './RowStatus';

/** Island families (schema 1.6.0, `Proposal.family`, the engine's French names) → their label in the UI language. */
const FAMILY_KEYS: Record<string, MessageKey> = {
  façonnage: 'review.family.shaping',
  'façonnage aux marqueurs': 'review.family.markers',
  division: 'review.family.division',
  attente: 'review.family.holders',
  montage: 'review.family.start',
  compte: 'review.family.count',
  longueur: 'review.family.length',
  renvoi: 'review.family.reference',
};

/** Slots of a reading (`Proposal.filled` keys, `Proposal.holes`: the engine's pack classes) → their label. */
const SLOT_KEYS: Record<string, MessageKey> = {
  'N:sts': 'review.slot.sts',
  'N:rows': 'review.slot.rows',
  'N:times': 'review.slot.times',
  'N:cm': 'review.slot.cm',
  'N:rowref': 'review.slot.rowref',
  'N:delta': 'review.slot.delta',
  'N:yo': 'review.slot.yo',
  'N:markers': 'review.slot.markers',
  MARKER: 'review.slot.markers',
  SHAPE_VERB: 'review.slot.shape',
  BIND_OFF_VERB: 'review.slot.bindOff',
  EACH_END: 'review.slot.place',
  AT_PLACE: 'review.slot.place',
  AT_MARKERS: 'review.slot.place',
  EVERY: 'review.slot.every',
  HOLDER: 'review.slot.holder',
  HOLD_VERB: 'review.slot.holder',
  UNTIL: 'review.slot.until',
  UNTIL_REMAIN: 'review.slot.until',
  CAST_ON_VERB: 'review.slot.start',
  COUNT_INTRO: 'review.slot.count',
  CONTINUE_VERB: 'review.slot.continue',
  REP_VERB: 'review.slot.repeat',
};

/** A family the UI has no label for is shown as the engine names it (in French), as a code. */
function FamilyName({ name, t }: { name: string; t: Translate }) {
  const key = FAMILY_KEYS[name.trim().toLowerCase()];
  if (key) return <>{t(key)}</>;
  return (
    <code className="font-mono" data-verbatim lang="fr">
      {name}
    </code>
  );
}

/** A slot's label, or its pack class name (« N:sts ») when the UI has none. */
function slotLabel(slot: string, t: Translate): string {
  const key = SLOT_KEYS[slot.trim()];
  return key ? t(key) : slot;
}

/**
 * « Lignes à relire »: the lines the engine did not read for the selected size (or any), sorted by what downstream
 * can tell of them (`lib/review.ts`), each with its proposals by rank. A proposal is shown and can be copied; it is
 * never applied (the engine proposes, the designer decides).
 */
export const ReviewList = memo(function ReviewList({
  result,
  entries,
  selectedSize,
  onJump,
}: {
  result: CompileResult;
  entries: ReviewEntry[];
  selectedSize: number | null;
  onJump: (line: number) => void;
}) {
  const { t } = useI18n();
  if (result.rows.length === 0) {
    return (
      <p className="text-sm text-muted" data-testid="review-empty">
        {t('review.noRows')}
      </p>
    );
  }
  if (entries.length === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-accent-strong" data-testid="review-empty">
        <Icon name="check" size={14} />
        {t('review.empty')}
      </p>
    );
  }
  return (
    <div className="space-y-5">
      <p className="max-w-prose text-sm leading-relaxed text-muted">{t('review.intro')}</p>
      <ol className="space-y-4" data-testid="review-list">
        {entries.map((entry) => (
          <li key={entry.lineNo}>
            <ReviewCard entry={entry} result={result} selectedSize={selectedSize} onJump={onJump} />
          </li>
        ))}
      </ol>
    </div>
  );
});

function ReviewCard({
  entry,
  result,
  selectedSize,
  onJump,
}: {
  entry: ReviewEntry;
  result: CompileResult;
  selectedSize: number | null;
  onJump: (line: number) => void;
}) {
  const { t, lang } = useI18n();
  const tone = rowTone(entry.row, selectedSize);
  const docSizes = result.document.sizes.length;
  const concerned = entry.sizes.map((s) => s.size);
  const patternLang = result.document.lang;
  const reason = reviewReason(entry, t, lang);
  return (
    <article
      className="print-break-avoid rounded-card bg-surface px-6 py-5"
      data-testid="review-entry"
      data-line={entry.lineNo}
      data-category={entry.category}
      data-tone={tone ?? undefined}
      aria-label={`${t('diag.line', { n: entry.lineNo })} · ${t(`review.cat.${entry.category}`)}`}
    >
      <button
        type="button"
        onClick={() => onJump(entry.lineNo)}
        title={t('diag.goto', { n: entry.lineNo })}
        className="group -mx-1 block w-[calc(100%+0.5rem)] rounded-control px-1 text-left"
        data-testid="review-jump"
      >
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <ToneGlyph tone={tone} className="text-md" />
          <RowLabel lineNo={entry.lineNo} label={entry.row.label} result={result} className="font-display text-lg font-[480] text-fg" />
          <span className="inline-flex items-center gap-1 text-xs text-muted group-hover:text-fg group-hover:underline">
            {t('diag.line', { n: entry.lineNo })}
            <Icon name="arrowRight" size={11} />
          </span>
          <span className="ml-auto inline-flex flex-wrap items-center gap-1">
            {docSizes > 1 && concerned.length < docSizes && <SizeBadges sizes={concerned} result={result} />}
            <span className="whitespace-nowrap rounded-pill bg-sunken px-2 py-0.5 text-2xs font-semibold text-muted" data-testid="review-category">
              {t(`review.cat.${entry.category}`)}
            </span>
          </span>
        </span>
        {entry.text !== null && (
          <span className="mt-3 block rounded-control bg-editor px-4 py-3">
            <code className="verbatim block whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-fg" data-verbatim lang={patternLang} data-testid="review-line">
              {entry.text}
            </code>
          </span>
        )}
        <span className="mt-3 block text-sm leading-relaxed text-fg" data-testid="review-reason">
          {reason}
        </span>
      </button>

      <div className="mt-4 space-y-2 pl-0.5" data-testid="proposals">
        {entry.proposals.length === 0 ? (
          <p className="text-xs italic text-subtle" data-testid="review-no-proposal">
            {t('review.noProposal')}
          </p>
        ) : (
          <>
            <div className="kicker">{entry.proposals.length === 1 ? t('review.proposals1') : t('review.proposalsN', { n: entry.proposals.length })}</div>
            <ol className="space-y-2">
              {entry.proposals.map((p) => (
                <ProposalItem key={`${p.rank}-${p.canonical}`} proposal={p} />
              ))}
            </ol>
          </>
        )}
      </div>
    </article>
  );
}

/** One reading: rank, family, the canonical form with its holes in sight, what filled what, and « Copier ». */
function ProposalItem({ proposal: p }: { proposal: Proposal }) {
  const { t } = useI18n();
  const fromOracle = oracleSlots(p);
  return (
    <li
      className="rounded-control border border-line px-3 py-2"
      data-testid="proposal"
      data-rank={p.rank}
      data-family={p.family}
      data-reconciles={p.reconciles === null ? 'null' : String(p.reconciles)}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="tabular text-2xs font-bold text-subtle" title={t('review.rank', { n: p.rank })}>
          {p.rank}.
        </span>
        <span className="rounded-pill bg-sunken px-2 py-0.5 text-2xs font-semibold text-muted" data-testid="proposal-family">
          <FamilyName name={p.family} t={t} />
        </span>
        {p.reconciles === true && (
          <span className="inline-flex items-center gap-1 text-2xs font-semibold text-muted" data-testid="proposal-reconciles">
            <span aria-hidden="true" className="text-success">
              ✓
            </span>
            {t('review.reconciles')}
          </span>
        )}
        {p.reconciles === false && (
          <span className="inline-flex items-center gap-1 text-2xs font-semibold text-muted" data-testid="proposal-reconciles">
            <span aria-hidden="true" className="text-error">
              ✗
            </span>
            {t('review.reconcilesNot')}
          </span>
        )}
        <CopyButton text={p.canonical} rank={p.rank} />
      </div>
      <code className="mt-1.5 block whitespace-pre-wrap break-words font-mono text-[12px] text-fg" lang="en" data-testid="proposal-canonical">
        {canonicalParts(p.canonical).map((part, i) =>
          part.hole ? (
            <mark key={i} className="rounded bg-warning/15 px-0.5 font-bold text-warning" title={t('review.holeTitle')} data-testid="proposal-hole">
              ?
            </mark>
          ) : (
            <span key={i}>{part.text}</span>
          ),
        )}
      </code>
      {(fromOracle.length > 0 || p.holes.length > 0) && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {fromOracle.map((slot) => (
            <span key={`o-${slot}`} className="rounded-pill bg-info/10 px-2 py-0.5 text-2xs font-semibold text-info" data-testid="proposal-oracle-slot" data-slot={slot}>
              {t('review.oracleSlot', { slot: slotLabel(slot, t) })}
            </span>
          ))}
          {p.holes.map((slot) => (
            <span key={`h-${slot}`} className="rounded-pill bg-warning/[0.12] px-2 py-0.5 text-2xs font-semibold text-warning" data-testid="proposal-hole-slot" data-slot={slot}>
              <span aria-hidden="true">? </span>
              {t('review.holeSlot', { slot: slotLabel(slot, t) })}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

/** « Copier »: the reading goes to the clipboard, nothing else happens (no edit of the pattern, ever). */
function CopyButton({ text, rank }: { text: string; rank: number }) {
  const { t } = useI18n();
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');
  useEffect(() => {
    if (state === 'idle') return;
    const id = window.setTimeout(() => setState('idle'), 1800);
    return () => window.clearTimeout(id);
  }, [state]);
  const copy = async () => {
    try {
      if (!navigator.clipboard) throw new Error('no clipboard');
      await navigator.clipboard.writeText(text);
      setState('done');
    } catch {
      setState('failed');
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="btn-quiet ml-auto h-7 text-2xs"
      aria-label={t('review.copyAria', { rank, text })}
      title={t('review.copyTitle')}
      data-testid="proposal-copy"
      data-state={state}
    >
      <Icon name={state === 'done' ? 'check' : 'copy'} size={12} />
      <span aria-live="polite">{state === 'done' ? t('review.copied') : state === 'failed' ? t('review.copyFailed') : t('review.copy')}</span>
    </button>
  );
}
